import { PAYMENT_METHOD } from '@hedepy/common/enums/PAYMENT_METHOD'
import React, { createContext, useContext, useState, type FC, type SetStateAction } from 'react'

import { type ReserveSlotType } from '../../../hooks/useEventActions/interface'
import { type ServiceType } from '../../../types/GeneralStateType'
import { type TherapistType } from '../../../types/TherapistStateType'
import { PAYMENT_SCREEN } from '../../reservation/types'
import usePayment from './usePayment'

interface IPaymentContext {
  readonly handleOnPayment: (paymentMethod: string) => void
  readonly handleSendVoucher: (paymentMethod: string) => void
  readonly handleReserveWithSubscription: (userSubscriptionId: number) => void
  readonly handleReserveWithBenefits: (companyEventQuotaId: number) => void
  readonly service: ServiceType & { price: number }
  readonly therapist: TherapistType
  readonly slot: ReserveSlotType
  readonly isPending: boolean
  readonly selectedScreen: PAYMENT_SCREEN
  readonly setSelectedScreen: React.Dispatch<SetStateAction<PAYMENT_SCREEN>>
  readonly selectedPaymentMethod: PAYMENT_METHOD
  readonly setSelectedPaymentMethod: (value: PAYMENT_METHOD) => void
  readonly voucherCode: string
  readonly setVoucherCode: React.Dispatch<SetStateAction<string>>
  readonly discountedPrice: number
}

const PaymentContext = createContext<IPaymentContext>({
  handleOnPayment: () => {},
  handleSendVoucher: () => {},
  handleReserveWithSubscription: () => {},
  handleReserveWithBenefits: () => {},
  isPending: false,
  service: null,
  therapist: null,
  slot: null,
  selectedScreen: PAYMENT_SCREEN.DEFAULT,
  setSelectedScreen: () => {},
  selectedPaymentMethod: PAYMENT_METHOD.CARD_ALL,
  setSelectedPaymentMethod: () => {},
  voucherCode: null,
  setVoucherCode: () => {},
  discountedPrice: null,
})

export const usePaymentContext = (): IPaymentContext => useContext(PaymentContext)

interface PaymentProviderProps {
  readonly children: React.ReactNode
  readonly slot: ReserveSlotType
  readonly therapist: TherapistType
  readonly onCloseModal: () => void
  readonly service: ServiceType & { price: number }
  readonly status?: number
}

export const PaymentContextProvider: FC<PaymentProviderProps> = ({
  children,
  slot,
  therapist,
  service,
  onCloseModal,
}) => {
  const [selectedScreen, setSelectedScreen] = useState<PAYMENT_SCREEN>(PAYMENT_SCREEN.DEFAULT)
  const [selectedPaymentMethod, setSelectedPaymentMethodInternal] = useState<PAYMENT_METHOD>(PAYMENT_METHOD.CARD_ALL)
  const {
    handleOnPayment,
    handleSendVoucher,
    handleReserveWithSubscription,
    handleReserveWithBenefits,
    isPending,
    voucherCode,
    setVoucherCode,
    discountedPrice,
  } = usePayment({
    slot,
    therapist,
    setSelectedScreen,
    onCloseModal,
  })

  const setSelectedPaymentMethod = (value: PAYMENT_METHOD) => {
    setSelectedPaymentMethodInternal(value)
    if (value === PAYMENT_METHOD.MASTERCARD) {
      setVoucherCode('')
    }
  }

  return (
    <PaymentContext.Provider
      value={{
        service,
        slot,
        therapist,
        handleOnPayment,
        handleSendVoucher,
        handleReserveWithSubscription,
        handleReserveWithBenefits,
        isPending,
        selectedScreen,
        setSelectedScreen,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        voucherCode,
        setVoucherCode,
        discountedPrice,
      }}
    >
      {children}
    </PaymentContext.Provider>
  )
}
